import { moduleSlot } from "./moduleLayout";

export function electricalStrings(design) {
  return Object.entries(design || {}).flatMap(([key, mppts], inv) => (mppts || []).flatMap((m, mi) =>
    (m.inputs || []).flatMap((input, ii) => {
      const modules = Number(input.modules_per_string ?? input.modulesPerString);
      const mppt = Number(m.index ?? mi + 1), index = Number(input.index ?? ii + 1);
      return modules > 0 ? [{ inverter: inv + 1, inverter_model: key, mppt, input: index, modules,
        string_id: `INV-${String(inv + 1).padStart(2, "0")}-MPPT-${String(mppt).padStart(2, "0")}-IN-${String(index).padStart(2, "0")}` }] : [];
    })));
}

// A plan edit never routes a different module into a deleted module's place.
// The original slot-to-string links survive until an electrical design is applied.
export function planStringAssignments({ systemDesign, rowModules, tiers = 1, excludedSlots = [], assignments = [] }) {
  const excluded = new Set(excludedSlots);
  const slots = rowModules.flatMap((columns, row) => Array.from({ length: Number(columns) * tiers }, (_, i) => ({
    row: row + 1, column: Math.floor(i / tiers), tier: i % tiers,
  }))).filter(p => !excluded.has(moduleSlot(p)));
  const present = new Set(slots.map(moduleSlot)), occupied = new Set(), ids = new Set();
  const strings = electricalStrings(systemDesign).map(s => {
    const module_positions = assignments.filter(p => p.string_id === s.string_id && present.has(moduleSlot(p)))
      .filter(p => {
        if (occupied.has(moduleSlot(p)) || ids.has(p.module_id)) return false;
        occupied.add(moduleSlot(p)); ids.add(p.module_id); return true;
      });
    return { ...s, module_positions, present_modules: module_positions.length,
      missing_modules: Math.max(0, s.modules - module_positions.length),
      row: module_positions[0]?.row ?? null, column: module_positions[0]?.column ?? null };
  });
  const unassigned = slots.filter(p => !occupied.has(moduleSlot(p)));
  const rows = rowModules.map((columns, i) => ({ row: i + 1, columns: Number(columns), tiers,
    segments: strings.flatMap(s => {
      const positions = s.module_positions.filter(p => p.row === i + 1);
      const segments = [];
      for (const p of positions.sort((a, b) => a.column - b.column || a.tier - b.tier)) {
        const column = p.column + p.tier / tiers, last = segments[segments.length - 1];
        if (last && Math.abs(last.column + last.columns - column) < 1e-8) {
          last.columns += 1 / tiers; last.modules++;
        } else segments.push({ ...s, module_positions: undefined, column, columns: 1 / tiers,
          modules: 1, incomplete: s.missing_modules > 0 });
      }
      return segments;
    }), excluded: excludedSlots.filter(k => k.startsWith(`${i + 1}:`)),
    unassigned: unassigned.filter(p => p.row === i + 1),
  }));
  const missing = strings.reduce((n, s) => n + s.missing_modules, 0);
  const expected = strings.reduce((n, s) => n + s.modules, 0);
  const consistent = missing === 0 && unassigned.length === 0 && expected === occupied.size;
  return { strings, rows, unassigned, inverters: [...new Set(strings.map(s => s.inverter))],
    problems: [], manualPlan: true,
    check: { consistent, string_modules: expected, field_slots: slots.length, placed_modules: occupied.size,
      missing_modules: missing, unplaced_strings: strings.filter(s => s.missing_modules).length, empty_slots: unassigned.length },
  };
}
